import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Star, HardHat, PackagePlus, PackageSearch, Armchair, PackageOpen,
  ArrowDownLeft, CircleUserRound, AlertTriangle, Recycle, Trophy, Users, ArrowRight,
  RotateCcw, ImageDown, UploadCloud, Store, UserRound,
} from "lucide-react";
import { TANGRAM_BASE_V1, TANGRAM_COLORS } from "@/lib/tangram";
import {
  PIECE_IDS, ROT_CENTERS, ZERO_LAYOUT, normalizeLayout, rotatePoint,
  resolveDrop, buildSnapshotSvg, boardBBox,
} from "@/lib/tangramBoard";

// True polygon centroids — icon placed EXACTLY here; label directly below, centered on the same axis.
const CENTROIDS = {
  s3: { cx: 916.667, cy: 250 },
  s4: { cx: 750, cy: 500 },
  s5: { cx: 500, cy: 666.667 },
  s6: { cx: 375, cy: 875 },
};
const QA_ICON = 62;
function centroidBox({ cx, cy }, w = 230, h = 150) {
  return { x: cx - w / 2, y: cy - QA_ICON / 2, w, h };
}

const JAKARTA = "'Plus Jakarta Sans', 'Inter', sans-serif";
// Sprint 16 — refined icon container: softer inset ring, subtle inner glow, thicker
// backdrop blur; icons themselves use a slightly bolder stroke for legibility.
const iconWrap = {
  width: 54, height: 54, borderRadius: "50%",
  background: "linear-gradient(180deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.14) 100%)",
  border: "1px solid rgba(255,255,255,0.55)",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.35), 0 4px 14px rgba(0,0,0,0.10)",
  display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(3px)",
};
const qaLabel = { fontFamily: JAKARTA, fontWeight: 700, fontSize: 27, lineHeight: 1.02, letterSpacing: "-0.035em", color: "#fff", marginTop: 12 };

const CATS = {
  alert: { label: "Alerta", color: "#C98A2E", Icon: AlertTriangle },
  opportunity: { label: "Oportunidad", color: "#2E9E5B", Icon: Recycle },
  achievement: { label: "Logro", color: "#2F72B0", Icon: Trophy },
  community: { label: "Comunidad", color: "#7C5FC7", Icon: Users },
};
const S2_BASE = "#1FA9A3";

function daysUntil(dateStr) {
  const t = new Date(); t.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round((new Date(`${dateStr}T00:00:00`) - t) / 86400000));
}

const SNAP_EASE = "transform .28s cubic-bezier(.22,1,.36,1)";

// A tangram piece: base geometry NEVER changes; the user transform {dx,dy,rot}
// is applied to the polygon while the label stays horizontal (translate-only).
function PieceShape({ id, t, dragging, fill, label, onPointerDown, onActivate }) {
  const c0 = ROT_CENTERS[id];
  const trans = dragging ? "none" : SNAP_EASE;
  return (
    <g
      className={`tangram-piece${dragging ? " dragging" : ""}`}
      role="button"
      tabIndex={0}
      aria-label={label}
      data-testid={`tangram-piece-${id}`}
      onPointerDown={(e) => onPointerDown(id, e)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onActivate(id); } }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <polygon
        points={TANGRAM_BASE_V1[id]}
        fill={fill}
        stroke="#FAF7F2"
        strokeWidth="14"
        strokeLinejoin="miter"
        style={{ transition: `fill .8s ease, ${trans}`, transform: `translate(${t.dx || 0}px, ${t.dy || 0}px) rotate(${t.rot || 0}deg)`, transformOrigin: `${c0.x}px ${c0.y}px` }}
      />
    </g>
  );
}

// Label layer: always horizontal, always painted above every polygon.
function PieceLabel({ id, t, dragging, box, children, align = "center" }) {
  const c0 = ROT_CENTERS[id];
  const anchor = align === "top"
    ? [box.x + box.w / 2, box.y + QA_ICON / 2]
    : [box.x + box.w / 2, box.y + box.h / 2];
  const ra = rotatePoint(anchor, c0, t.rot || 0);
  const lx = ra[0] + (t.dx || 0) - anchor[0];
  const ly = ra[1] + (t.dy || 0) - anchor[1];
  const trans = dragging ? "none" : SNAP_EASE;
  return (
    <foreignObject x={box.x} y={box.y} width={box.w} height={box.h} style={{ pointerEvents: "none", transition: trans, transform: `translate(${lx}px, ${ly}px)` }}>
      <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: align === "top" ? "flex-start" : "center", textAlign: "center" }}>
        {children}
      </div>
    </foreignObject>
  );
}

function QuickAction({ Icon, lines }) {
  return (
    <>
      <div style={{ ...iconWrap, width: QA_ICON, height: QA_ICON }}><Icon size={34} color="#fff" strokeWidth={2.1} /></div>
      <div style={{ ...qaLabel, fontSize: 28, marginTop: 12 }}>{lines.map((l, i) => <div key={i}>{l}</div>)}</div>
    </>
  );
}

function box({ cx, cy, w, h }) { return { x: cx - w / 2, y: cy - h / 2, w, h }; }

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [layout, setLayout] = useState(ZERO_LAYOUT);
  const [dragId, setDragId] = useState(null);
  const layoutRef = useRef(layout);
  layoutRef.current = layout;
  const svgRef = useRef(null);
  const boxRef = useRef(null);
  const dragRef = useRef(null);
  const saveTimer = useRef(null);
  const bboxRef = useRef(boardBBox(ZERO_LAYOUT));
  // Freeze the visible area while dragging; expand/contract it on drop.
  if (!dragId) bboxRef.current = boardBBox(layout);
  const bb = bboxRef.current;

  useEffect(() => {
    if (user?.id) api.get(`/employees/${user.id}`).then(({ data }) => setMe(data)).catch(() => {});
  }, [user?.id]);

  useEffect(() => {
    api.get("/lobby/layout").then(({ data }) => {
      if (data?.layout) setLayout(normalizeLayout(data.layout));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    Promise.all([
      api.get("/notifications").then((r) => r.data).catch(() => []),
      api.get("/assets", { params: { is_surplus: true } }).then((r) => r.data).catch(() => []),
      api.get("/assets", { params: { availability: "available_soon", is_surplus: false } }).then((r) => r.data).catch(() => []),
      api.get("/leaderboard", { params: { sort: "points" } }).then((r) => r.data).catch(() => []),
      api.get("/projects").then((r) => r.data).catch(() => []),
    ]).then(([notifs, surplus, soon, leaders, projects]) => {
      const list = [];
      const unread = (notifs || []).filter((n) => !n.read);
      if (unread.length) {
        list.push({ cat: "alert", title: unread[0].text, sentence: "Toca para revisar la conversación.", link: unread[0].link || "/" });
      }
      const sc = (surplus || []).find((s) => s.project_region === "centro") || (surplus || [])[0];
      if (sc) {
        list.push({ cat: "opportunity", title: `${sc.quantity} ${sc.unit} de ${sc.name.toLowerCase()} disponibles`, sentence: "Reutiliza antes de comprar y suma puntos a tu proyecto.", link: "/surplus" });
      } else if (soon?.length) {
        const a = [...soon].sort((x, y) => (x.available_date || "").localeCompare(y.available_date || ""))[0];
        list.push({ cat: "opportunity", title: `${a.quantity} ${a.name.toLowerCase()} se liberan pronto`, sentence: `Disponibles en ${daysUntil(a.available_date)} días en ${a.project_name}.`, link: "/search" });
      }
      if (leaders?.[0]) {
        list.push({ cat: "achievement", title: `${leaders[0].name} alcanzó ${leaders[0].points.toLocaleString()} puntos`, sentence: "Lidera el ranking de reutilización de la empresa.", link: "/leaderboard" });
      }
      if (projects?.length) {
        const p = projects[projects.length - 1];
        list.push({ cat: "community", title: `Proyecto ${p.name}`, sentence: "Colabora, comparte recursos y evita compras nuevas.", link: "/leaderboard" });
      }
      setItems(list.slice(0, 3));
      setIdx(0);
    });
  }, []);

  useEffect(() => {
    if (items.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 9000);
    return () => clearInterval(t);
  }, [items.length]);

  const persist = () => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.put("/lobby/layout", { layout: layoutRef.current }).catch(() => {});
    }, 600);
  };

  const setPieceT = (id, t) => setLayout((l) => ({ ...l, [id]: t }));

  const rotatePiece = (id, duringDrag = false) => {
    const cur = layoutRef.current[id];
    const rotated = { ...cur, rot: ((cur.rot || 0) + 45) % 360 };
    if (duringDrag) { setPieceT(id, rotated); return; }
    setPieceT(id, resolveDrop(id, rotated, layoutRef.current));
    persist();
  };

  const toSvg = (e) => {
    const pt = svgRef.current.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    return pt.matrixTransform(svgRef.current.getScreenCTM().inverse());
  };

  const activate = (id) => {
    const map = {
      s1: () => nav(user?.id ? `/employees/${user.id}` : "/me"),
      s2: () => nav(items[idx]?.link || "/novedades"),
      s3: () => nav("/upload"),
      s4: () => nav("/employees"),
      s5: () => nav("/equip-office"),
      s6: () => nav("/surplus"),
      s7: () => nav("/mi-espacio"),
    };
    map[id]?.();
  };

  const onPieceDown = (id, e) => {
    if (e.button === 2) { rotatePiece(id, !!dragRef.current && dragRef.current.id === id); e.preventDefault(); return; }
    if (dragRef.current) return; // second pointer handled at svg level
    e.preventDefault();
    const start = toSvg(e);
    dragRef.current = { id, pointerId: e.pointerId, start, t0: { ...layoutRef.current[id] }, moved: false };

    const move = (ev) => {
      const d = dragRef.current;
      if (!d || ev.pointerId !== d.pointerId) return;
      const p = toSvg(ev);
      const dx = p.x - d.start.x, dy = p.y - d.start.y;
      if (!d.moved && Math.hypot(dx, dy) > 10) { d.moved = true; setDragId(d.id); }
      if (d.moved) {
        const cur = layoutRef.current[d.id];
        setPieceT(d.id, { dx: d.t0.dx + dx, dy: d.t0.dy + dy, rot: cur.rot });
      }
    };
    const up = (ev) => {
      const d = dragRef.current;
      if (!d || ev.pointerId !== d.pointerId) return;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      dragRef.current = null;
      setDragId(null);
      if (!d.moved) { activate(d.id); return; }
      setPieceT(d.id, resolveDrop(d.id, layoutRef.current[d.id], layoutRef.current));
      persist();
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  };

  // Second finger/pointer while holding a piece → rotate it 45°.
  const onSvgPointerDown = (e) => {
    const d = dragRef.current;
    if (d && e.pointerId !== d.pointerId) {
      e.preventDefault();
      rotatePiece(d.id, true);
    }
  };

  const resetBoard = () => {
    setLayout(ZERO_LAYOUT);
    clearTimeout(saveTimer.current);
    api.delete("/lobby/layout").catch(() => {});
    boxRef.current?.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    toast.success("Tablero restablecido");
  };

  const shareSnapshot = async () => {
    try {
      const colors = { ...TANGRAM_COLORS, s2: s2Fill };
      const { svg, w, h } = buildSnapshotSvg(layoutRef.current, colors);
      const url = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml;charset=utf-8" }));
      const img = new Image();
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
      const scale = 1400 / w;
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(w * scale);
      canvas.height = Math.round(h * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      const file = new File([blob], "mi-tangram.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Mi Tangram" });
      } else {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "mi-tangram.png";
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 4000);
      }
      toast.success("Instantánea lista para guardar");
    } catch (err) {
      if (err?.name !== "AbortError") toast.error("No se pudo generar la instantánea");
    }
  };

  const displayName = user?.display_name || user?.alias || user?.name || "";
  const firstName = displayName ? displayName.split(" ")[0] : "";
  const active = items[idx] || null;
  const cat = active ? CATS[active.cat] : null;
  const s2Fill = cat ? cat.color : S2_BASE;

  const pieces = [
    {
      id: "s1", fill: "#F5B841", label: `hola ${firstName}`, box: box({ cx: 500, cy: 195, w: 560, h: 250 }), align: "center",
      content: (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 30 }}>
          {user?.photo ? (
            <img src={user.photo} alt="avatar" style={{ width: 124, height: 124, borderRadius: "50%", objectFit: "cover", border: "6px solid rgba(255,255,255,0.82)", boxShadow: "0 10px 26px rgba(10,37,64,0.28)" }} />
          ) : (
            <div style={{ width: 124, height: 124, borderRadius: "50%", background: "#0A2540", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 54, fontFamily: JAKARTA, border: "6px solid rgba(255,255,255,0.82)", boxShadow: "0 10px 26px rgba(10,37,64,0.28)" }}>
              {firstName?.[0]?.toUpperCase() || "A"}
            </div>
          )}
          <div style={{ textAlign: "left" }}>
            <div style={{ fontFamily: JAKARTA, fontSize: 24, fontWeight: 500, color: "#0A2540", opacity: 0.7, letterSpacing: "0.02em" }}>hola</div>
            <div style={{ fontFamily: JAKARTA, fontSize: 54, fontWeight: 800, color: "#0A2540", letterSpacing: "-0.03em", lineHeight: 1 }}>{firstName}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 26, marginTop: 18, color: "#0A2540" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 12, fontWeight: 800, fontSize: 36, fontFamily: JAKARTA }} title="Proyectos"><HardHat size={38} strokeWidth={2.1} /> {me?.projects_count ?? 0}</span>
              <span style={{ width: 2, height: 34, background: "rgba(10,37,64,0.22)", borderRadius: 2 }} />
              <span style={{ display: "inline-flex", alignItems: "center", gap: 12, fontWeight: 800, fontSize: 36, fontFamily: JAKARTA }} title="Puntos"><Star size={38} fill="#0A2540" strokeWidth={0} /> {(me?.total_points ?? 0).toLocaleString()}</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "s2", fill: s2Fill, label: "novedades", box: box({ cx: 178, cy: 500, w: 300, h: 380 }), align: "center",
      content: (
        <>
          {cat && (
            <div key={idx} className="news-fade" style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", textAlign: "left", width: "100%", maxWidth: 262 }}>
              <div style={{ ...iconWrap, width: 56, height: 56 }}><cat.Icon size={28} color="#fff" strokeWidth={2.1} /></div>
              <div style={{ fontFamily: JAKARTA, fontSize: 15, fontWeight: 700, color: "#fff", opacity: 0.85, textTransform: "uppercase", letterSpacing: "0.16em", marginTop: 16 }}>{cat.label}</div>
              <div style={{ fontFamily: JAKARTA, fontSize: 30, fontWeight: 800, color: "#fff", lineHeight: 1.08, letterSpacing: "-0.02em", marginTop: 8 }}>{active.title}</div>
              <div style={{ fontFamily: JAKARTA, fontSize: 16, fontWeight: 500, color: "#fff", opacity: 0.9, lineHeight: 1.3, marginTop: 12 }}>{active.sentence}</div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: JAKARTA, fontSize: 16, fontWeight: 700, color: "#fff", marginTop: 16 }}>Ver más <ArrowRight size={16} strokeWidth={2.4} /></div>
            </div>
          )}
          {!cat && (
            <div style={{ fontFamily: JAKARTA, fontSize: 22, fontWeight: 600, color: "#fff", opacity: 0.9 }}>Sin novedades por ahora</div>
          )}
          {items.length > 1 && (
            <div style={{ display: "flex", gap: 6, marginTop: 24, width: "100%", maxWidth: 262, pointerEvents: "auto" }}>
              {items.map((_, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                  onPointerDown={(e) => e.stopPropagation()}
                  aria-label={`Ver elemento ${i + 1}`}
                  data-testid={`lobby-hero-dot-${i}`}
                  style={{ flex: 1, height: 4, borderRadius: 999, border: "none", padding: 0, cursor: "pointer", background: "rgba(255,255,255,0.35)", overflow: "hidden" }}
                >
                  <span style={{ display: "block", height: "100%", borderRadius: 999, background: "#fff", width: i === idx ? "100%" : "0%", transition: i === idx ? "width 9s linear" : "width .3s" }} />
                </button>
              ))}
            </div>
          )}
        </>
      ),
    },
    {
      id: "s3", fill: "#46B15D", label: "Sube", box: centroidBox(CENTROIDS.s3, 180, 150), align: "top",
      content: <QuickAction Icon={UploadCloud} lines={["Sube"]} />,
    },
    {
      id: "s4", fill: "#3B82F6", label: "People", box: centroidBox(CENTROIDS.s4, 240, 150), align: "top",
      content: <QuickAction Icon={Users} lines={["People"]} />,
    },
    {
      id: "s5", fill: "#2E9E5B", label: "Reutiliza", box: centroidBox({ cx: 500, cy: 636 }, 220, 150), align: "top",
      content: <QuickAction Icon={Recycle} lines={["Reutiliza"]} />,
    },
    {
      id: "s6", fill: "#8B5CF6", label: "Market", box: centroidBox(CENTROIDS.s6, 240, 150), align: "top",
      content: <QuickAction Icon={Store} lines={["Market"]} />,
    },
    {
      id: "s7", fill: "#F97316", label: "mi espacio", box: box({ cx: 815, cy: 828, w: 240, h: 140 }), align: "center",
      content: (
        <>
          <div style={iconWrap}><UserRound size={28} color="#fff" strokeWidth={2.1} /></div>
          <div style={{ ...qaLabel, marginTop: 12 }}>mi espacio</div>
          <ArrowDownLeft size={14} color="#fff" strokeWidth={2.25} style={{ opacity: 0.5, marginTop: 9 }} />
        </>
      ),
    },
  ];

  // Dragged piece paints last (on top).
  const ordered = dragId ? [...pieces.filter((p) => p.id !== dragId), pieces.find((p) => p.id === dragId)] : pieces;

  return (
    <div className="tangram-wrap" style={{ flexDirection: "column", gap: 10 }} data-testid="dashboard-page">
      <div className="tangram-box" ref={boxRef} data-testid="tangram-scroll-area">
        <svg
          ref={svgRef}
          viewBox={`${bb.x} ${bb.y} ${bb.w} ${bb.h}`}
          preserveAspectRatio="xMidYMid meet"
          role="group"
          aria-label="Menú principal tangram"
          style={{ display: "block", width: `${bb.w / 10}%`, height: `${bb.h / 10}%`, overflow: "visible" }}
          onPointerDown={onSvgPointerDown}
          onContextMenu={(e) => e.preventDefault()}
        >
          {ordered.map((p) => (
            <PieceShape
              key={p.id}
              id={p.id}
              t={layout[p.id]}
              dragging={dragId === p.id}
              fill={p.fill}
              label={p.label}
              onPointerDown={onPieceDown}
              onActivate={activate}
            />
          ))}
          {ordered.map((p) => (
            <PieceLabel key={`lbl-${p.id}`} id={p.id} t={layout[p.id]} dragging={dragId === p.id} box={p.box} align={p.align}>
              {p.content}
            </PieceLabel>
          ))}
        </svg>
      </div>
      <div style={{ display: "flex", gap: 14, justifyContent: "center" }}>
        <button className="lobby-tool-btn" onClick={resetBoard} data-testid="lobby-reset-btn" aria-label="Restablecer figura">
          <RotateCcw size={13} strokeWidth={2} /> restablecer figura
        </button>
        <button className="lobby-tool-btn" onClick={shareSnapshot} data-testid="lobby-share-btn" aria-label="Compartir instantánea">
          <ImageDown size={13} strokeWidth={2} /> compartir instantánea
        </button>
      </div>
    </div>
  );
}
